/**
 * Prices the hotel price sidebar from the visitor's device.
 *
 * The sidebar ships from the generator with a spinner in place of a price, and the
 * edge fills that spinner in when it has a cached price to give. When it does not,
 * this runs: it asks the Hotel Price Service for the hotel's Hotel Price Cache
 * Entry and replaces the spinner with either a price or the "currently
 * unavailable" block.
 *
 * It asks our own service rather than running a search itself, which is what makes
 * the fill *shared*: the service writes what it finds into the cache, so one
 * visitor's fill serves every later visitor through the edge, on any device. A
 * browser running its own search helps nobody but the browser that ran it.
 *
 * The price is read from the entry's per-person field — the same field the edge
 * reads out of the same entry (`lambdas/static-site-origin-lambda/src/hotel-price.ts`
 * in `cloudfront-sundeals`). That is the point of reading it here rather than a
 * rounded average off a raw search result: with one fetcher and one field, a hotel
 * cannot be priced differently depending on whether the cache happened to be warm.
 *
 * It lives apart from `main.ts` so it can be driven directly in a test. The
 * bootstrap keeps the wiring — finding the sidebars, observing them, firing once
 * when one scrolls into view — and everything that decides what to request and what
 * to render lives here.
 *
 * Its collaborators arrive as {@link HotelPriceFillDeps} rather than as imports so
 * that a test needs neither a network nor a `document`: `i18n` reads the page's
 * injected script tags when it loads, and `fetch` would put a real request behind
 * every assertion.
 *
 * **Every path ends in rendered markup.** A failure, an error response and a
 * request that outlives its deadline all render the unavailable block, because the
 * caller is observer wiring with nowhere to put a rejection and a visitor left
 * watching a spinner that will never stop is worse than being told there is no
 * price. The imprecision is accepted and recorded in the spec: the visitor is told
 * "no prices available" when the truth is "we could not find out". The honest
 * wording is a separate copy change, and nothing here blocks it.
 *
 * **There is no retry.** A failed fill is dropped and the next page view tries
 * again. Retrying would turn a supplier outage into a retry storm from every open
 * tab, which is the exact load pattern this work exists to remove.
 *
 * The hotel identifier is read from `data-giata-id`, the one attribute the slimmed
 * sidebar writes, falling back to the `data-hotel-codes` array the pages already in
 * S3 carry. See {@link readGiataId} for why both, and when the fallback can go.
 *
 * @module hotel-price-fill
 */
import {
  buildPricingSection,
  type Pricing,
} from '@sundeals/unit-pricing-sidebar';

/**
 * The part of a Hotel Price Cache Entry this reads.
 *
 * Narrower and more forgiving than the service's own type, because it arrives as
 * parsed JSON from another service across a version boundary: the deployed page
 * doing the asking may be older or newer than the service answering. Everything is
 * therefore optional and checked, not declared and trusted.
 *
 * `productData: null` is the answer "no bookable holiday was found" — a successful
 * answer about a hotel, not a failure to get one.
 */
export interface HotelPriceEntry {
  productData?: {
    price?: { currentPrice?: number; currentPricePerPerson?: number };
    urlPath?: string;
  } | null;
  available?: boolean;
  /**
   * Part of the entry and deliberately not read. The deals link is built from the
   * sidebar's own `data-destination-id`, because it has to be buildable on the
   * paths where there is no entry — a failure, a timeout, a sidebar naming no
   * hotel. Declared so the shape being parsed is the shape being documented.
   */
  destinationGiataId?: number | string | null;
}

/** The part of `Response` this uses, so a test can answer without one. */
export interface HotelPriceHttpResponse {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}

export interface HotelPriceFillDeps {
  /** Makes the request. In the browser this is `fetch`. */
  request: (
    url: string,
    init: { signal: AbortSignal }
  ) => Promise<HotelPriceHttpResponse>;
  getLocale: () => string;
  /** The site's origin, which the browser build supplies from the environment. */
  siteUrl: string;
  /** Overridable for tests. See {@link DEFAULT_TIMEOUT_MS}. */
  timeoutMs?: number;
}

/**
 * How long a fill is given before it is given up on.
 *
 * Generous rather than tight, because the deadline is there to stop a hung request
 * holding the spinner forever, not to enforce a latency target. Production prices a
 * hotel in about 111ms on average, but the non-production environments show a 95th
 * percentile around seven seconds, and a deadline that failed most non-prod fills
 * would make the feature untestable where it is tested.
 */
const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Accepts an identifier only if the service would.
 *
 * Checked against the same rule the service applies (`^\d+$`, non-zero) rather than
 * trusted, for two reasons: the value becomes a URL path segment, and a request that
 * the service is certain to refuse is a round trip spent to learn what was knowable
 * here.
 */
function validGiataId(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;

  const giataId = String(value);
  return /^\d+$/.test(giataId) && giataId !== '0' ? giataId : undefined;
}

/**
 * Reads the hotel out of the old `data-hotel-codes` JSON array.
 *
 * The array is a leftover of the Search Configuration the browser used to run,
 * holding exactly one identifier — the page's own hotel.
 */
function fromHotelCodes(raw: string | undefined): string | undefined {
  if (!raw) return undefined;

  let codes: unknown;
  try {
    codes = JSON.parse(raw);
  } catch {
    return undefined;
  }

  return validGiataId(Array.isArray(codes) ? codes[0] : undefined);
}

/**
 * Reads the hotel this sidebar is about, or nothing if it does not name one.
 *
 * **Two shapes, on purpose.** `data-giata-id` is what the template writes today.
 * `data-hotel-codes` is what the pages already sitting in S3 carry, and a deploy
 * does not regenerate them — so for as long as regeneration takes, most of the site
 * is serving the old shape. Reading only the new one would look correct and quietly
 * stop those pages filling; reading only the old one would keep a slimmed page from
 * ever filling. Both are read, the new one first, and the fallback can go once no
 * page in S3 predates the slimming.
 *
 * Anything neither shape names identifiably is treated as "this sidebar names no
 * hotel", which renders the unavailable block without asking anybody.
 */
function readGiataId(element: HTMLElement): string | undefined {
  return (
    validGiataId(element.dataset.giataId) ??
    fromHotelCodes(element.dataset.hotelCodes)
  );
}

/**
 * Asks the Hotel Price Service for one entry, or fails inside the deadline.
 *
 * The deadline is enforced here rather than left to the request, because a request
 * that never settles is exactly the case it exists for. The signal is passed on as
 * well as raced, so a given-up-on request is actually cancelled instead of
 * continuing to occupy the connection it no longer has a reader for.
 */
async function requestEntry(
  url: string,
  deps: HotelPriceFillDeps,
  timeoutMs: number
): Promise<HotelPriceEntry> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const answer = (async () => {
    const response = await deps.request(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Hotel price request failed with ${response.status}`);
    }
    return (await response.json()) as HotelPriceEntry;
  })();

  // The deadline wins the race, so a rejection arriving from an abandoned request
  // afterwards has no reader. Claimed here rather than left unhandled.
  answer.catch(() => {});

  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`Hotel price request timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([answer, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Turns an entry into what the sidebar renders.
 *
 * Deliberately the same reading the edge does: the per-person amount off the
 * product data, availability off the entry's own flag, and the booking link off the
 * product data's path. Product data present with no per-person amount still renders
 * the available block at zero, as the edge does, because the search declares the
 * amount required and inventing a third state for a schema violation nobody has
 * seen would be inventing it in two places.
 *
 * One place it cannot follow the edge: a payload that is not an entry at all. The
 * edge calls that `unreadable` and renders the *loading* state, on the grounds that
 * asserting "unavailable" off something it could not read asserts more than it
 * knows — it can afford that, because the browser is behind it. Here there is
 * nothing behind, and loading is the state this exists to leave, so an unreadable
 * payload renders unavailable like any other thing that did not answer.
 */
function pricingFrom(
  entry: HotelPriceEntry,
  locale: string,
  siteUrl: string
): Pricing {
  const { productData } = entry;

  // Null or absent product data is the "no bookable holiday" answer.
  if (!productData || typeof productData !== 'object') {
    return { PricePerPerson: 0, Available: false, Url: '' };
  }

  return {
    PricePerPerson: productData.price?.currentPricePerPerson || 0,
    Available: entry.available ?? false,
    Url: `${siteUrl}/${locale.toLowerCase()}/hotels/${productData.urlPath}`,
  };
}

/** What renders when nothing is known: the unavailable block's pricing. */
const NOTHING_KNOWN: Pricing = {
  PricePerPerson: 0,
  Available: false,
  Url: '',
};

/**
 * Fills the sidebar's price from the Hotel Price Service, and always settles.
 *
 * Does nothing at all when the spinner is absent, which is how a price the edge
 * already filled in is left alone — and is why a warm page makes no request.
 */
export async function fillHotelPrice(
  element: HTMLElement,
  deps: HotelPriceFillDeps
): Promise<void> {
  const loading = element.querySelector(
    '[data-hotel-price-loading]'
  ) as HTMLElement | null;

  if (!loading) return;

  const locale = deps.getLocale();
  const lowerLocale = locale.toLowerCase();
  const giataId = readGiataId(element);

  let pricing = NOTHING_KNOWN;

  if (giataId) {
    try {
      const entry = await requestEntry(
        // Relative on purpose: the service is exposed through the CDN on the
        // site's own domain, so this is same-origin and needs no preflight.
        `/hotel-price/${giataId}/${lowerLocale}`,
        deps,
        deps.timeoutMs ?? DEFAULT_TIMEOUT_MS
      );
      pricing = pricingFrom(entry ?? {}, locale, deps.siteUrl);
    } catch (error) {
      // Settle rather than propagate, and do not ask again. See the module note.
      console.warn(
        `Could not fill the price for hotel ${giataId}:`,
        error instanceof Error ? error.message : error
      );
    }
  } else {
    console.warn('A hotel price sidebar names no hotel; not filling it.');
  }

  // The destination comes off the sidebar rather than off the entry, because the
  // deals link has to be buildable on the paths where there is no entry to read.
  const destinationGiataId = element.dataset.destinationId;

  try {
    loading.parentElement!.innerHTML = buildPricingSection(
      locale,
      pricing,
      `${deps.siteUrl}/${lowerLocale}/results?destinations=${destinationGiataId}`
    );
  } catch (error) {
    // Rendering is not obviously fallible, but it is not a string template either:
    // the renderer loads translations and formats an ICU message. If it throws
    // there is nothing left to render *with*, so the spinner does stay — but the
    // caller is observer wiring that drops the promise, and an unhandled rejection
    // would make this the one failure nobody could see.
    console.error('Could not render the hotel price sidebar:', error);
  }
}
